// src/services/cotizadorHistorial.js
// Historial de precios por destino (adenda "Revendedor", sección 5). Funciones
// puras: sin UI ni Supabase. Los costos llegan con todos sus decimales y se
// redondea solo al mostrar (igual que en cotizadorCalc.js).
import { diaNum, fmtFecha, fmtBRL } from './cotizadorCalc';

export const DIAS_SIN_DATOS = 14; // pasados estos días no se muestra rango

// ── Ciudad ───────────────────────────────────────────────────────────
// Sin mayúsculas, sin tildes, sin "+ ómnibus" ni códigos "(CWB)".
export function normalizarCiudad(texto) {
  return String(texto || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/\(.*?\)/g, ' ')
    .replace(/\s*\+.*$/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

// "Manaus (MAO) → GRU → Curitiba (CWB), 2 conexões" -> "Curitiba"
export function destinoDeRuta(ruta) {
  const partes = String(ruta || '').split(/→|->/);
  const ultimo = (partes[partes.length - 1] || '').split(',')[0];
  return ultimo.replace(/\(.*?\)/g, ' ').replace(/\s+/g, ' ').trim();
}

// Cantidad de escalas de la ruta: 0 = directo, null = no se sabe.
export function escalasDeRuta(ruta) {
  const t = String(ruta || '');
  const m = t.match(/(\d+)\s*(?:paradas?|conex\S*|escalas?)/i);
  if (m) return Number(m[1]);
  if (/direct[oa]/i.test(t)) return 0;
  const saltos = t.split(/→|->/).length - 1;
  return saltos > 1 ? saltos - 1 : null;
}

export const cubetaEscalas = (e) => (e === null || e === undefined ? 'sin' : e === 0 ? 'directo' : e === 1 ? 'una' : 'dos');
export const CUBETAS = [
  { id: 'directo', label: 'Directo' },
  { id: 'una', label: '1 escala' },
  { id: 'dos', label: '2 o más escalas' },
  { id: 'sin', label: 'Escalas sin dato' },
];

// ── Estadística ──────────────────────────────────────────────────────
export function mediana(valores) {
  const v = valores.filter((x) => x !== null && x !== undefined && !Number.isNaN(x)).sort((a, b) => a - b);
  if (!v.length) return null;
  const m = Math.floor(v.length / 2);
  return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2;
}

const estadisticas = (regs) => {
  const costos = regs.map((r) => r.costo);
  return costos.length
    ? { n: costos.length, min: Math.min(...costos), mediana: mediana(costos), max: Math.max(...costos) }
    : { n: 0, min: null, mediana: null, max: null };
};

// Cuántos días pasaron entre la fecha de la cotización y hoy (ISO 'YYYY-MM-DD').
const edadDias = (fechaCot, hoy) => {
  const a = diaNum(fechaCot);
  const b = diaNum(hoy);
  return a === null || b === null ? null : b - a;
};

// Resumen de un destino. `registros` = todos los del destino (ya filtrados).
// Registro: { costo, fechaCot, fechaVuelo, aerolinea, fuente, escalas, ... }
export function resumenDestino(registros, hoy, ventanaDias = 21) {
  const conEdad = registros
    .map((r) => ({ ...r, edad: edadDias(r.fechaCot, hoy) }))
    .filter((r) => r.edad !== null);
  const ordenados = [...conEdad].sort((a, b) => a.edad - b.edad); // más recientes primero
  const enVentana = ordenados.filter((r) => r.edad >= 0 && r.edad <= ventanaDias);

  const porCubeta = {};
  CUBETAS.forEach((c) => { porCubeta[c.id] = estadisticas(enVentana.filter((r) => cubetaEscalas(r.escalas) === c.id)); });

  // Tendencia: mediana de los últimos 7 días contra la de los 7 anteriores.
  const med = (desde, hasta) => mediana(ordenados.filter((r) => r.edad >= desde && r.edad < hasta).map((r) => r.costo));
  const reciente = med(0, 7);
  const previa = med(7, 14);
  const tendencia = reciente !== null && previa !== null && previa > 0 ? reciente / previa - 1 : null;

  const ultima = ordenados[0] || null;
  return {
    total: conEdad.length,
    ventana: estadisticas(enVentana),
    porCubeta,
    tendencia,
    ultima,
    ultimas: ordenados.slice(0, 5).map((r) => ({
      ...r,
      anticipacion: diaNum(r.fechaVuelo) !== null && diaNum(r.fechaCot) !== null ? diaNum(r.fechaVuelo) - diaNum(r.fechaCot) : null,
    })),
  };
}

// Agrupa por destino normalizado. Devuelve [{ clave, nombre, registros }].
export function agruparPorDestino(registros) {
  const mapa = new Map();
  registros.forEach((r) => {
    const clave = normalizarCiudad(r.destino);
    if (!clave) return;
    if (!mapa.has(clave)) mapa.set(clave, { clave, nombre: r.destino, registros: [] });
    mapa.get(clave).registros.push(r);
  });
  return [...mapa.values()];
}

// Una cotización guardada puede duplicarse y repetir sus filas: se cuentan una vez.
export function quitarRepetidos(registros) {
  const vistos = new Set();
  return registros.filter((r) => {
    const k = [normalizarCiudad(r.destino), r.fechaCot, r.fechaVuelo, r.aerolinea, r.escalas, Math.round(r.costo * 100)].join('|');
    if (vistos.has(k)) return false;
    vistos.add(k);
    return true;
  });
}

// Línea que se muestra en la pantalla de una opción. Devuelve
// { texto, gris } o null si no hay historial del destino.
export function lineaReferencia(registros, ciudad, hoy, ventanaDias = 21) {
  const clave = normalizarCiudad(ciudad);
  if (!clave) return null;
  const propios = registros.filter((r) => normalizarCiudad(r.destino) === clave);
  if (!propios.length) return null;
  const nombre = String(ciudad).replace(/\s*\+.*$/, '').trim();
  const res = resumenDestino(propios, hoy, ventanaDias);
  const v = res.ventana;
  if (!res.ultima || res.ultima.edad > DIAS_SIN_DATOS || v.n === 0) {
    const u = res.ultima;
    return { gris: true, texto: `Sin datos recientes. Última: ${fmtFecha(u.fechaCot)}, R$ ${fmtBRL(u.costo, 0)}` };
  }
  return {
    gris: false,
    texto: `${nombre}, últimos ${ventanaDias} días: R$ ${fmtBRL(v.min, 0)} a R$ ${fmtBRL(v.max, 0)} (mediana ${fmtBRL(v.mediana, 0)}, ${v.n} ${v.n === 1 ? 'cotización' : 'cotizaciones'}). Solo una guía: el precio cambia con la fecha del vuelo y con lo cerca que esté.`,
  };
}

// ── CSV ──────────────────────────────────────────────────────────────
// Lector mínimo (comillas dobles, comas y saltos de línea dentro de comillas).
export function parsearCSV(texto) {
  const filas = [];
  let fila = [];
  let campo = '';
  let comillas = false;
  const t = String(texto || '').replace(/^﻿/, '');
  for (let i = 0; i < t.length; i += 1) {
    const c = t[i];
    if (comillas) {
      if (c === '"' && t[i + 1] === '"') { campo += '"'; i += 1; }
      else if (c === '"') comillas = false;
      else campo += c;
    } else if (c === '"') comillas = true;
    else if (c === ',') { fila.push(campo); campo = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && t[i + 1] === '\n') i += 1;
      fila.push(campo); campo = '';
      if (fila.some((x) => x !== '')) filas.push(fila);
      fila = [];
    } else campo += c;
  }
  fila.push(campo);
  if (fila.some((x) => x !== '')) filas.push(fila);
  return filas;
}

// "5015,43" · "1.533,69" · "1533.69" -> número
export function numeroDeTexto(v) {
  const s = String(v ?? '').trim().replace(/[^\d.,-]/g, '');
  if (!s) return null;
  const n = s.includes(',') ? Number(s.replace(/\./g, '').replace(',', '.')) : Number(s);
  return Number.isNaN(n) ? null : n;
}

// Convierte el CSV en registros para guardar. Cada fila es un precio de una
// fuente; las fuentes de una misma búsqueda (misma cotización, ruta, fecha de
// vuelo y aerolínea) se quedan con la más barata, que es lo que el Cotizador
// llama mejor costo. Si la nota dice "3 adultos", el costo se divide entre 3:
// el historial guarda el precio de UN pasajero.
export function registrosDeCSV(texto) {
  const filas = parsearCSV(texto);
  if (filas.length < 2) return { registros: [], descartadas: 0 };
  const cab = filas[0].map((h) => h.trim().toLowerCase());
  const col = (n) => cab.indexOf(n);
  const iFc = col('fecha_cotizacion');
  const iRuta = col('ruta');
  const iFv = col('fecha_vuelo');
  const iAer = col('aerolinea');
  const iFu = col('fuente');
  const iCosto = col('costo');
  const iNota = col('nota');
  if ([iFc, iRuta, iFv, iCosto].some((i) => i < 0)) throw new Error('Faltan columnas: fecha_cotizacion, ruta, fecha_vuelo y costo.');

  let descartadas = 0;
  const grupos = new Map();
  filas.slice(1).forEach((f) => {
    const fechaCot = String(f[iFc] || '').trim().slice(0, 10);
    const fechaVuelo = String(f[iFv] || '').trim().slice(0, 10);
    const ruta = String(f[iRuta] || '').trim();
    const destino = destinoDeRuta(ruta);
    let costo = numeroDeTexto(f[iCosto]);
    if (diaNum(fechaCot) === null || !destino || costo === null || costo <= 0) { descartadas += 1; return; }
    const adultos = iNota >= 0 ? Number((String(f[iNota] || '').match(/(\d+)\s*adultos?/i) || [])[1]) : 0;
    const pasajeros = adultos > 1 ? adultos : 1;
    costo /= pasajeros;
    const aerolinea = iAer >= 0 ? String(f[iAer] || '').trim() : '';
    const fuente = iFu >= 0 ? String(f[iFu] || '').trim() : '';
    const clave = [String(f[iFc] || '').trim(), ruta, fechaVuelo, aerolinea].join('|').toLowerCase();
    const previo = grupos.get(clave);
    if (!previo || costo < previo.costo) {
      grupos.set(clave, {
        clave, destino, ruta, fechaCot, fechaVuelo, aerolinea, fuente, costo, pasajeros, escalas: escalasDeRuta(ruta),
      });
    }
  });
  return { registros: [...grupos.values()], descartadas };
}
